import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProgressBar } from "../../components/ui";
import { nextBand, START_BAND, TEST_LENGTH, type LevelAnswer, type LevelAnswerKind } from "../../lib/level-test";
import type { Word } from "../../data/words";

const BAND_LABEL: Record<number, string> = {
  1: "基礎 · 約 1,500 詞級",
  2: "基礎 · 約 2,000 詞級",
  3: "進階 · 約 3,000 詞級",
  4: "進階 · 約 4,000 詞級",
  5: "高階 · 約 5,500 詞級",
};

const OPTIONS: { kind: LevelAnswerKind; label: string }[] = [
  { kind: "know", label: "認識，能說出意思" },
  { kind: "unsure", label: "見過，但不確定" },
  { kind: "dont", label: "完全不認識" },
];

/** 从当前 band 未答过的词中抽;该 band 用尽时回退到任意未答过的词 */
function pickWord(pool: Word[], band: number, used: ReadonlySet<string>): Word {
  const unused = pool.filter((w) => !used.has(w.id));
  const inBand = unused.filter((w) => w.band === band);
  const src = inBand.length > 0 ? inBand : unused.length > 0 ? unused : pool;
  return src[Math.floor(Math.random() * src.length)];
}

export function Test({ pool }: { pool: Word[] }) {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<LevelAnswer[]>([]);
  const [band, setBand] = useState(START_BAND);
  const [used, setUsed] = useState<ReadonlySet<string>>(new Set());
  const [word, setWord] = useState<Word>(() => pickWord(pool, START_BAND, new Set()));

  const qNum = answers.length + 1;

  function answer(kind: LevelAnswerKind) {
    const nextAnswers = [...answers, { band: word.band, answer: kind }];
    if (nextAnswers.length >= TEST_LENGTH) {
      navigate("/level-test/result", { state: { answers: nextAnswers } });
      return;
    }
    const nextBandValue = nextBand(band, kind);
    const nextUsed = new Set(used);
    nextUsed.add(word.id);
    setAnswers(nextAnswers);
    setBand(nextBandValue);
    setUsed(nextUsed);
    setWord(pickWord(pool, nextBandValue, nextUsed));
  }

  return (
    <div className="min-h-dvh box-border bg-cream flex flex-col pt-[60px] px-[max(26px,5vw)] pb-[34px]">
      <div className="flex items-center gap-[14px]">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-[19px] text-ink3 bg-transparent border-0 p-0 cursor-pointer"
        >
          ✕
        </button>
        <div className="flex-1">
          <ProgressBar pct={(answers.length / TEST_LENGTH) * 100} />
        </div>
        <span className="font-grotesk text-[12.5px] font-semibold text-ink2">{qNum} / {TEST_LENGTH}</span>
      </div>

      <div className="text-center mt-[30px]">
        <span className="font-grotesk text-[12px] font-semibold text-accent-deep bg-accent-bg border border-accent-line px-[12px] py-[5px] rounded-full">
          {BAND_LABEL[word.band]}
        </span>
      </div>

      <div className="text-center pt-[74px]">
        <div className="text-[13.5px] text-ink2 mb-[22px]">這個詞你認識嗎？</div>
        <div className="font-grotesk text-[44px] font-semibold tracking-[-.025em] text-ink">{word.word}</div>
        <div className="font-phon text-[17px] text-ink3 mt-[8px]">{word.phonetic}</div>
      </div>

      <div className="mt-auto flex flex-col gap-[9px]">
        {OPTIONS.map((opt) => (
          <button
            key={opt.kind}
            type="button"
            onClick={() => answer(opt.kind)}
            className={`p-[16px] rounded-[14px] text-center cursor-pointer ${
              opt.kind === "know" ? "bg-ink border-0" : "bg-white border border-line"
            }`}
          >
            <div className={`text-[16px] font-bold ${opt.kind === "know" ? "text-cream" : "text-ink"}`}>
              {opt.label}
            </div>
          </button>
        ))}
        <p className="mx-[4px] mt-[12px] mb-0 font-grotesk text-[12.5px] leading-[1.55] text-ink3 text-center">
          難度會跟著你的答案走 — 答對就更難。
        </p>
      </div>
    </div>
  );
}
